import { useMemo } from 'react'

import type { ExecutionResponse, ExecutionStatus } from '@/features/controller/restart/types'
import { formatSmsStatus, formatSshStatus } from '@/features/controller/utils/restartStatusFormatters'

/**
 * Calculates restart progress percentage and status text for the progress bar.
 * Only returns meaningful values when a restart is actively in progress.
 *
 * @param isRestarting - Whether the restart is currently in progress
 * @param lastRestart - The latest restart execution data from the API
 * @returns Object containing progress percentage (0-100) and human-readable status text
 */
export function useRestartProgress(
  isRestarting: boolean,
  lastRestart: ExecutionResponse | undefined,
): { progress: number; statusText: string } {
  return useMemo(() => {
    if (!isRestarting || !lastRestart) {
      return { progress: 0, statusText: '' }
    }

    const status = lastRestart.status

    // Helper to get detailed status from history
    const getDetailedStatus = (): string => {
      const statusDetails = lastRestart.statusDetails
      if (!statusDetails) return ''

      // Get the latest history entry
      if ((statusDetails.sshStatusHistory?.length ?? 0) > 0) {
        const latestSshStatus = statusDetails.sshStatusHistory![statusDetails.sshStatusHistory!.length - 1]
        return formatSshStatus(latestSshStatus.status)
      }

      if ((statusDetails.smsStatusHistory?.length ?? 0) > 0) {
        const latestSmsStatus = statusDetails.smsStatusHistory![statusDetails.smsStatusHistory!.length - 1]
        return formatSmsStatus(latestSmsStatus.status)
      }

      return ''
    }

    // Map execution status to progress and text
    const progressMap: Record<
      ExecutionStatus,
      { progress: number; statusText: string } | (() => { progress: number; statusText: string })
    > = {
      QUEUED: { progress: 10, statusText: 'Queued' },
      IN_PROGRESS: () => {
        const detailedStatus = getDetailedStatus()
        const statusText = detailedStatus ? `In Progress: ${detailedStatus}` : 'In Progress'
        return { progress: 50, statusText }
      },
      SUCCEEDED: { progress: 100, statusText: 'Completed' },
      FAILED: { progress: 100, statusText: 'Failed' },
      TIMED_OUT: { progress: 100, statusText: 'Timed Out' },
      CANCELED: { progress: 100, statusText: 'Canceled' },
      REJECTED: { progress: 100, statusText: 'Rejected' },
      REMOVED: { progress: 100, statusText: 'Removed' },
    }

    const result = progressMap[status]
    if (typeof result === 'function') {
      return result()
    }
    return result || { progress: 0, statusText: '' }
  }, [isRestarting, lastRestart])
}
